import get from 'lodash/get';
import appData from './appData';

// During build, every instance of `process.env` gets replaced with the current
// `process.env` object. This centralizes that replacement to one location,
// from which we can reference anywhere, thereby reducing that duplication.
export const processEnv = process.env;

/**
 * Gets the requested `process.env` object property.
 *
 * Optionally, returns a fallback, rather than `null`, if passed.
 *
 * @param {string} key      The `process.env` property to retrieve.
 * @param {*}      fallback The fallback value.
 *
 * @returns {*} The `process.env[key]` value.
 */
export const getProcessEnv = (key, fallback) => {
	return get(processEnv, key, fallback || null);
};

// Get the current `process.env.NODE_ENV`, falling back to `production` as default.
export const currentNodeEnv = getProcessEnv('NODE_ENV', 'production').toLowerCase();

// Use `currentNodeEnv` as it's a little more descriptive/distinct.
export const currentEnv = currentNodeEnv;

/**
 * Get an environment variable, with an optional fallback.
 *
 * @since  2.0.0
 *
 * @deprecated Use `getProcessEnv` instead.
 *
 * @param  {string} key      The env variable to get.
 * @param  {mixed}  fallback The optional fallback value.
 *
 * @returns {mixed}           The result.
 */
export const getVar = (key, fallback) => {
	return getProcessEnv(key, fallback);
};

/**
 * Normalize environment string for comparisons.
 *
 * @param {string} env The env to normalize.
 *
 * @returns {string} One of `dev`, `beta`, `staging`, `production`. Defaults to `production`.
 */
export const normalizeEnv = (env) => {
	env = env.toLowerCase();
	switch (env) {
		case 'development':
			return 'dev';
		case 'prod':
			return 'production';
		default:
			return env;
	}
};

/**
 * Returns the environment string for the current environment.
 *
 * Checks to see if the `ENV` environment variable has been passed from
 * `wp-content/.env`, and attached to `window.app.data.ENV`.
 *
 * @returns {string} One of `dev`, `beta`, `staging`, `production`. Defaults to `production`.
 */
export const getEnv = () => {
	// Check the window app data for a current environment.
	return normalizeEnv(appData.get('ENV', currentNodeEnv));
};

/**
 * Check if we are on a given environment.
 *
 * @since  0.1.4
 *
 * @param  {string} compare The env to check.
 *
 * @returns {bool}         Whether we are on the given environment.
 */
export const isEnv = (compare) => {
	return getEnv() === normalizeEnv(compare);
};

/**
 * Returns true if the current environment the development environment.
 *
 * @returns {boolean} True if current environment is development.
 */
export const isDevelopment = () => {
	return isEnv('dev');
};

/**
 * Returns true if the current environment the beta environment.
 *
 * @returns {boolean} True if current environment is beta.
 */
export const isBeta = () => {
	return isEnv('beta');
};

/**
 * Returns true if the current environment the staging environment.
 *
 * @returns {boolean} True if current environment is staging.
 */
export const isStaging = () => {
	return isEnv('staging');
};

/**
 * Returns true if the current environment the production environment.
 *
 * @returns {boolean} True if current environment is production.
 */
export const isProduction = () => {
	return isEnv('production');
};
